import React, { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Loader2, Send, Search } from 'lucide-react';
import { toast } from 'sonner';

/** Coordinator reviews the school's parents before any ask goes out.
 *  Driven either by an unmet request (booking) or by a career coverage row (target). */
export default function AskParentsDialog({ booking, target, onOpenChange, onSent }) {
  const open = !!(booking || target);
  const isProfession = target?.kind === 'profession';
  const candidatesUrl = booking
    ? `/coordinator/unfulfilled-requests/${booking.booking_id}/ask-candidates`
    : isProfession
      ? `/coordinator/coverage-candidates?profession_label=${encodeURIComponent(target?.profession_label || '')}`
      : `/coordinator/coverage-candidates?career_slug=${encodeURIComponent(target?.career_slug || '')}`;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [search, setSearch] = useState('');
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    if (!open) return;
    setLoading(true);
    setSelected(new Set());
    setSearch('');
    try {
      const r = await api.get(candidatesUrl);
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load parents');
      setData(null);
    }
    setLoading(false);
  }, [open, candidatesUrl]);

  useEffect(() => { load(); }, [load]);

  const candidates = (data?.candidates || []).filter(c => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (c.parent_name || '').toLowerCase().includes(q)
      || (c.profession || '').toLowerCase().includes(q)
      || (c.parent_email || '').toLowerCase().includes(q);
  });

  const toggle = (email) => {
    const next = new Set(selected);
    if (next.has(email)) next.delete(email); else next.add(email);
    setSelected(next);
  };

  const send = async () => {
    setSending(true);
    try {
      const r = booking
        ? await api.post(`/coordinator/unfulfilled-requests/${booking.booking_id}/ask`, {
          parent_emails: [...selected],
        })
        : await api.post('/coordinator/coverage-ask', {
          career_slug: isProfession ? '' : (target?.career_slug || ''),
          profession_label: isProfession ? (target?.profession_label || '') : '',
          parent_emails: [...selected],
        });
      toast.success(r.data.message || 'Ask sent');
      onSent?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not send');
    }
    setSending(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] flex flex-col" data-testid="ask-parents-dialog">
        <DialogHeader>
          <DialogTitle className="font-['Outfit'] text-base">
            Ask parents about {data?.career_name || booking?.career_name || target?.career_name || 'this career'}
          </DialogTitle>
        </DialogHeader>
        <p className="text-sm text-[#6B7280] -mt-1">
          The email names {isProfession ? 'their profession' : 'the career'} only — never a student or a family.
          Nothing is sent until you press send.
        </p>

        {loading ? (
          <div className="py-12 text-center"><Loader2 className="h-5 w-5 animate-spin text-[#9CA3AF] mx-auto" /></div>
        ) : (
          <>
            <div className="relative mt-2">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[#9CA3AF]" />
              <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search name or profession"
                className="pl-9 h-9 text-sm rounded-lg border-[#E5E7EB]" data-testid="ask-search" />
            </div>
            <ScrollArea className="flex-1 max-h-[320px] mt-2 border border-[#E5E7EB] rounded-lg">
              {candidates.length === 0 ? (
                <p className="text-xs text-[#9CA3AF] text-center py-10" data-testid="ask-no-candidates">
                  {isProfession ? 'No parents left to ask about this profession.' : 'No other parents left to ask for this career.'}
                </p>
              ) : candidates.map(c => (
                <label key={c.parent_email}
                  className={`flex items-center gap-3 px-3 py-2.5 border-b border-[#F3F4F6] last:border-0 ${
                    c.already_asked ? 'opacity-60' : 'cursor-pointer hover:bg-[#FAFAFA]'}`}
                  data-testid={`ask-candidate-${c.parent_email}`}>
                  <input type="checkbox" checked={selected.has(c.parent_email)} onChange={() => toggle(c.parent_email)}
                    disabled={c.already_asked}
                    className="h-3.5 w-3.5 rounded border-[#D1D5DB] disabled:opacity-40" />
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm text-[#1F2937] truncate">{c.parent_name || c.parent_email}</span>
                    <span className="block text-[11px] text-[#9CA3AF] truncate">
                      {c.profession || 'Profession not recorded'}
                    </span>
                  </span>
                  {c.already_asked ? (
                    <Badge className="border-0 text-[10px] bg-[#F3F4F6] text-[#6B7280] shrink-0">Already asked</Badge>
                  ) : c.profession_matches ? (
                    <Badge className="border-0 text-[10px] bg-emerald-50 text-emerald-700 shrink-0">Profession match</Badge>
                  ) : null}
                </label>
              ))}
            </ScrollArea>
          </>
        )}

        <DialogFooter className="mt-3">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-lg text-sm h-9">Cancel</Button>
          <Button onClick={send} disabled={sending || selected.size === 0}
            className="rounded-lg text-sm h-9 bg-[#F97D6A] hover:bg-[#e86c5a] text-white" data-testid="ask-send-btn">
            {sending ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Send className="h-3.5 w-3.5 mr-1.5" />}
            Send to {selected.size || 'selected'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
